import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon, Linkedin, Github, Twitter } from "lucide-react";

// Theme toggle logic (for production, this should be in ThemeContext/useTheme)
const useTheme = () => {
  const [theme, setTheme] = useState(
    localStorage.getItem("theme") || "dark"
  );
  React.useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);
  const toggleTheme = () =>
    setTheme(theme === "dark" ? "light" : "dark");
  return { theme, toggleTheme };
};

const Navbar = () => {
  const [searchText, setSearchText] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  // Accessibility: close menu on ESC
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    if (menuOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [menuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchText.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchText.trim())}`);
      setSearchText("");
      setMenuOpen(false);
    }
  };

  // Social links (all point to Work In Progress page)
  const socials = [
    {
      name: "LinkedIn",
      url: "/wip",
      icon: <Linkedin size={18} aria-hidden="true" />,
    },
    {
      name: "GitHub",
      url: "/wip",
      icon: <Github size={18} aria-hidden="true" />,
    },
    {
      name: "Twitter",
      url: "/wip",
      icon: <Twitter size={18} aria-hidden="true" />,
    },
  ];

  // Dynamic icon background for theme toggle
  const iconBg =
    theme === "dark"
      ? "bg-slate-800 text-blue-400 hover:bg-slate-700 border-slate-600"
      : "bg-white text-blue-700 hover:bg-gray-100 border-gray-300";

  return (
    <nav
      className="fixed top-0 w-full bg-primary text-textDark dark:bg-slate-900 dark:text-textLight shadow-md z-50 transition-colors duration-300"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container mx-auto flex items-center justify-between px-4 py-3">

        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
        >
          <span className="text-4xl font-leagueSpartan font-medium tracking-tighter text-blue-700 dark:text-blue-400 transition-colors duration-300">
            BB
          </span>
          <h1 className="text-l font-openSans tracking-tight font-bold text-textDark dark:text-textLight">
            Blackbear
          </h1>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-6">
          {[
            { path: "/services", label: "Services" },
            { path: "/projects", label: "Projects" },
            { path: "/demo", label: "Demo" },
            { path: "/about", label: "About" },
            { path: "/contact", label: "Contact" },
          ].map(({ path, label }) => (
            <Link
              key={label}
              to={path}
              className="hover:text-blue-700 dark:hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded transition-colors"
            >
              {label}
            </Link>
          ))}

          {/* Search */}
          <form
            onSubmit={handleSearch}
            className="flex items-center ml-2 border border-textMuted dark:border-slate-700 rounded-lg px-4 py-1 bg-secondary dark:bg-slate-800 transition-colors"
          >
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Try Search..."
              aria-label="Search site"
              className="bg-transparent text-sm text-textDark dark:text-textLight placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none w-40"
            />
            <button
              type="submit"
              className="ml-2 text-sm hover:text-blue-700 dark:hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
            >
              Go →
            </button>
          </form>

          {/* Theme Toggle */}
          <button
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            onClick={toggleTheme}
            className={`ml-2 p-2 rounded border ${iconBg} focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors`}
            tabIndex={0}
            style={{
              boxShadow:
                theme === "light"
                  ? "0 0 0 2px #e0e7ef"
                  : "0 0 0 2px #1e293b",
              transition: "background 0.2s, color 0.2s",
            }}
          >
            {theme === "dark" ? (
              <Sun size={18} className="transition-colors" />
            ) : (
              <Moon size={18} className="transition-colors" />
            )}
          </button>

          {/* Social Media Icons (to /wip) */}
          <div className="ml-3 flex items-center gap-2">
            {socials.map(({ name, url, icon }) => (
              <Link
                key={name}
                to={url}
                aria-label={name}
                className="hover:text-blue-700 dark:hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded transition-colors"
              >
                {icon}
              </Link>
            ))}
          </div>
        </div>

        {/* Hamburger Icon */}
        <div className="md:hidden">
          <button
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMenuOpen(!menuOpen)}
            className="focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {menuOpen && (
        <div
          id="mobile-menu"
          className="md:hidden bg-primary dark:bg-slate-900 text-textDark dark:text-textLight px-4 pb-4 space-y-3 transition-colors"
          role="menu"
          aria-label="Mobile main menu"
        >
          {[
            { path: "/services", label: "Services" },
            { path: "/projects", label: "Projects" },
            { path: "/demo", label: "Demo" },
            { path: "/about", label: "About" },
            { path: "/contact", label: "Contact" },
          ].map(({ path, label }) => (
            <Link
              key={label}
              to={path}
              onClick={() => setMenuOpen(false)}
              className="block py-1 hover:text-blue-700 dark:hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded transition-colors"
            >
              {label}
            </Link>
          ))}

          {/* Search */}
          <form
            onSubmit={handleSearch}
            className="flex items-center border border-textMuted dark:border-slate-700 rounded-lg px-3 py-1 mt-2 bg-secondary dark:bg-slate-800 transition-colors"
          >
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Try Search..."
              aria-label="Search site"
              className="bg-transparent text-sm w-full text-textDark dark:text-textLight placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
            />
            <button
              type="submit"
              className="ml-2 text-sm hover:text-blue-700 dark:hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
            >
              Go →
            </button>
          </form>

          {/* Theme Toggle */}
          <button
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            onClick={toggleTheme}
            className={`mt-2 p-2 rounded border ${iconBg} focus:outline-none focus:ring-2 focus:ring-blue-400 bg-transparent transition-colors`}
            tabIndex={0}
            style={{
              boxShadow:
                theme === "light"
                  ? "0 0 0 2px #e0e7ef"
                  : "0 0 0 2px #1e293b",
              transition: "background 0.2s, color 0.2s",
            }}
          >
            {theme === "dark" ? (
              <Sun size={18} className="transition-colors" />
            ) : (
              <Moon size={18} className="transition-colors" />
            )}
          </button>

          {/* Social Media Icons (to /wip) */}
          <div className="mt-2 flex gap-3">
            {socials.map(({ name, url, icon }) => (
              <Link
                key={name}
                to={url}
                aria-label={name}
                className="hover:text-blue-700 dark:hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded transition-colors"
                onClick={() => setMenuOpen(false)}
              >
                {icon}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
